import React, { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabaseClient";

const INCIDENT_OPTIONS = [
  { value: "normal", label: "Resultado normal" },
  { value: "bye", label: "BYE / avance libre" },
  { value: "draw", label: "Empate" },
  { value: "forfeit", label: "Forfeit" },
  { value: "dq", label: "DQ" },
  { value: "no_show", label: "No-show" },
  { value: "manual", label: "Ajuste manual" },
];

const ADMIN_STATUS_OPTIONS = [
  { value: "active", label: "Activo" },
  { value: "drop", label: "Drop" },
  { value: "dq", label: "DQ" },
  { value: "no_show", label: "No-show" },
];

const PREL_PANEL_CLASS = "rounded-2xl border border-slate-200/80 bg-white/86 shadow-[0_20px_60px_rgba(15,23,42,0.08)] backdrop-blur";
const PREL_SOFT_PANEL_CLASS = "rounded-xl border border-slate-200/80 bg-[#f7fbff]/92";
const PREL_INPUT_CLASS = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 shadow-sm";
const PREL_DARK_TEXT_PANEL_CLASS = "rounded-2xl border border-slate-200/80 bg-white/92 p-4 shadow-[0_16px_40px_rgba(15,23,42,0.06)]";
const PREL_TABLE_HEAD_CLASS = "text-slate-500";
const PREL_TABLE_ROW_CLASS = "border-b border-slate-200/80";
const PREL_SECTION_CLASS = "rounded-[1.75rem] border border-slate-200/80 bg-white/94 p-5 shadow-[0_20px_50px_rgba(15,23,42,0.06)]";
const PREL_INSET_CLASS = "rounded-2xl border border-slate-200/80 bg-slate-50/70 p-4";

function SectionHeading({ eyebrow, title, description, aside = null }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-start sm:justify-between">
      <div>
        {eyebrow ? (
          <div className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#0f4cc9]/75">{eyebrow}</div>
        ) : null}
        <h3 className="mt-1 text-lg font-black text-slate-950">{title}</h3>
        {description ? <p className="mt-1 max-w-3xl text-sm leading-6 text-slate-600">{description}</p> : null}
      </div>
      {aside}
    </div>
  );
}

function SummaryStat({ label, value, tone = "default" }) {
  const toneClass =
    tone === "alert"
      ? "border-amber-200 bg-amber-50/90"
      : tone === "success"
        ? "border-emerald-200 bg-emerald-50/90"
        : "border-slate-200/80 bg-white/90";

  return (
    <div className={`rounded-2xl border p-3 shadow-sm ${toneClass}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-black text-slate-950">{value}</div>
    </div>
  );
}

function fmtDate(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return new Intl.DateTimeFormat("es-PR", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

function fmtSchedulePill(value) {
  if (!value) return "Fecha por definir";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Fecha por definir";
  return new Intl.DateTimeFormat("es-PR", {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function toDatetimeLocalValue(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const pad = (item) => String(item).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || "";
}

function statusTone(status) {
  if (status === "completed") return "border-emerald-300 bg-emerald-50 text-emerald-800";
  if (status === "bye") return "border-sky-300 bg-sky-50 text-sky-800";
  if (status === "empty") return "border-slate-200 bg-slate-50 text-slate-500";
  return "border-amber-300 bg-amber-50 text-amber-800";
}

function incidentLabel(value) {
  return INCIDENT_OPTIONS.find((item) => item.value === value)?.label || value || "Normal";
}

function stageLabel(value) {
  if (value === "swiss") return "Swiss";
  if (value === "round_robin") return "Round robin";
  if (value === "winners") return "Winners bracket";
  if (value === "losers") return "Losers bracket";
  if (value === "grand_final") return "Grand final";
  return "Top cut";
}

function tournamentModeLabel(value) {
  if (value === "swiss_top_cut") return "Swiss + Top cut";
  if (value === "double_elimination") return "Doble eliminacion";
  if (value === "round_robin") return "Round robin";
  return "Eliminacion simple";
}

function stageBoardModeMeta(mode) {
  if (mode === "swiss_top_cut") {
    return {
      accent: "from-cyan-400/30 via-sky-400/14 to-violet-500/24",
      badge: "Season to bracket",
      headline: "Swiss lane con cierre de top cut",
      description: "Mantiene el tramo suizo separado del bracket final para que producción pueda contar la historia correcta del torneo.",
    };
  }
  if (mode === "double_elimination") {
    return {
      accent: "from-fuchsia-500/28 via-violet-500/12 to-cyan-400/22",
      badge: "Upper + lower + final",
      headline: "Doble bracket con lectura de broadcast",
      description: "Winners, losers y grand final quedan aislados visualmente para que el flujo de cámara y caster desk no parezca un Swiss genérico.",
    };
  }
  if (mode === "round_robin") {
    return {
      accent: "from-emerald-400/24 via-cyan-400/12 to-sky-500/18",
      badge: "League schedule",
      headline: "Calendario claro para jornadas y standings",
      description: "La vista empuja pairing, agenda y progreso para formats tipo liga sin forzar una falsa llave de eliminación.",
    };
  }
  return {
    accent: "from-amber-400/24 via-orange-400/10 to-rose-500/18",
    badge: "Single elimination",
    headline: "Bracket principal listo para stream",
    description: "Cada ronda gana más peso visual para que semifinales y final respiren como una transmisión de escenario.",
  };
}

function nextRoundActionLabel(mode) {
  if (mode === "swiss_top_cut") return "Generar siguiente ronda swiss";
  if (mode === "round_robin") return "Generar calendario round robin";
  if (mode === "double_elimination") return "Generar winners y losers";
  return "Generar bracket principal";
}

function modeSetupInstruction(mode) {
  if (mode === "swiss_top_cut") return "Cuando la lista este lista, genera la primera ronda swiss.";
  if (mode === "round_robin") return "Cuando la lista este lista, genera el calendario de round robin.";
  if (mode === "double_elimination") return "Cuando la lista este lista, genera winners y deja listo el scaffold de losers.";
  return "Cuando la lista este lista, genera el bracket principal.";
}

function modeProgressInstruction(mode) {
  if (mode === "swiss_top_cut") return "Sigue corriendo Swiss hasta completar las rondas configuradas antes de abrir el top cut.";
  if (mode === "round_robin") return "Completa el calendario y usa standings para decidir cualquier cierre o playoff adicional.";
  if (mode === "double_elimination") return "Avanza winners, ajusta losers y reserva grand final cuando el cruce final este claro.";
  return "Sigue avanzando el bracket hasta definir semifinales, final y campeon.";
}

function normalizeSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .toLowerCase()
    .trim();
}

function buildDuplicateEntrantMap(entrants) {
  const counts = new Map();
  (entrants || []).forEach((entrant) => {
    const sourceKey = String(entrant.sourceEntryId || "").trim().toLowerCase();
    const nameKey = normalizeSearchText(entrant.displayName);
    const key = sourceKey ? `source:${sourceKey}` : `name:${nameKey}`;
    counts.set(key, (counts.get(key) || 0) + 1);
  });
  return counts;
}

function getCurrentRound(rounds) {
  return rounds.find((round) => round.matches?.some((match) => match.status !== "completed" && match.status !== "bye")) || null;
}

function getCompletedMatches(rounds) {
  return rounds.reduce(
    (total, round) =>
      total + round.matches.filter((match) => match.status === "completed" || match.status === "bye").length,
    0
  );
}

function getTotalMatches(rounds) {
  return rounds.reduce((total, round) => total + round.matches.length, 0);
}

function getSwissRounds(rounds) {
  return (rounds || []).filter((round) => round.stage === "swiss");
}

function hasPendingMatches(rounds) {
  return (rounds || []).some((round) => round.matches.some((match) => match.status === "pending"));
}

function getRoundIssueCount(round) {
  return (round?.matches || []).filter((match) => {
    const incident = String(match.incident || "").toLowerCase();
    const hasOperationalIncident = incident && !["normal", "bye", "draw"].includes(incident);
    const hasNotes = Boolean(String(match.notes || "").trim());
    return hasOperationalIncident || hasNotes;
  }).length;
}

function isRoundClosable(round) {
  if (!round || round.closedAt) return false;
  if (!Array.isArray(round.matches) || !round.matches.length) return false;
  return round.matches.every((match) => ["completed", "bye"].includes(String(match.status || "").toLowerCase()));
}

function findLatestClosableRound(rounds) {
  const closable = (rounds || []).filter((round) => isRoundClosable(round));
  return closable.length ? closable[closable.length - 1] : null;
}

function StaticPanel({ children, className = "" }) {
  return <div className={`${PREL_PANEL_CLASS} ${className}`}>{children}</div>;
}

function StageMatchCard({ match, entrantLookup, featured = false }) {
  const left = entrantLookup[match.leftEntrantId];
  const right = entrantLookup[match.rightEntrantId];
  const isComplete = match.status === "completed" || match.status === "bye";
  const winnerId = match.winnerEntrantId || "";
  const badgeClass = isComplete
    ? "border-emerald-300/60 bg-emerald-50 text-emerald-800"
    : "border-amber-300/70 bg-amber-50 text-amber-800";

  return (
    <article
      className={`relative overflow-hidden rounded-2xl border p-3 shadow-[0_12px_28px_rgba(15,23,42,0.12)] ${
        featured
          ? "border-violet-300/60 bg-[linear-gradient(135deg,rgba(15,23,42,0.98),rgba(42,16,84,0.92),rgba(91,33,182,0.88))] text-white"
          : "border-slate-200/80 bg-[linear-gradient(180deg,rgba(248,250,252,0.98),rgba(255,255,255,0.96))] text-slate-950"
      }`}
    >
      <div className={`pointer-events-none absolute inset-x-0 top-0 h-px ${featured ? "bg-gradient-to-r from-transparent via-cyan-300/80 to-transparent" : "bg-gradient-to-r from-transparent via-[#0f4cc9]/24 to-transparent"}`} />
      <div className="flex items-start justify-between gap-2">
        <div>
          <div className={`text-[10px] font-semibold uppercase tracking-[0.24em] ${featured ? "text-violet-200/90" : "text-slate-500"}`}>
            Match {match.matchNumber}
          </div>
          <div className={`mt-1 text-[11px] ${featured ? "text-violet-100/85" : "text-slate-500"}`}>
            {match.station || "Mesa pendiente"} · {fmtSchedulePill(match.scheduledAt)}
          </div>
        </div>
        <span className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${badgeClass}`}>
          {isComplete ? "Cerrado" : "En juego"}
        </span>
      </div>

      <div className="mt-3 space-y-2">
        {[
          { entrant: left, entrantId: match.leftEntrantId },
          { entrant: right, entrantId: match.rightEntrantId },
        ].map((slot, index) => {
          const isWinner = winnerId && winnerId === slot.entrantId;
          return (
            <div
              key={`${match.matchId}:${index}`}
              className={`rounded-xl border px-3 py-2 ${
                featured
                  ? isWinner
                    ? "border-cyan-300/80 bg-cyan-400/12 shadow-[0_0_0_1px_rgba(103,232,249,0.08)_inset]"
                    : "border-white/12 bg-white/6"
                  : isWinner
                    ? "border-emerald-300 bg-emerald-50 shadow-[0_0_0_1px_rgba(16,185,129,0.08)_inset]"
                    : "border-slate-200 bg-white/90"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold">{slot.entrant?.displayName || "TBD / BYE"}</div>
                <div className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${featured ? "text-violet-100/85" : "text-slate-500"}`}>
                  {isWinner ? "Winner" : slot.entrantId ? "Slot" : "Open"}
                </div>
              </div>
              <div className={`mt-1 text-[11px] ${featured ? "text-violet-100/80" : "text-slate-500"}`}>
                {slot.entrant?.subtitle || (slot.entrantId ? "Participante confirmado" : "Esperando avance")}
              </div>
            </div>
          );
        })}
      </div>

      <div className={`mt-3 flex items-center justify-between gap-2 text-[11px] ${featured ? "text-violet-100/85" : "text-slate-500"}`}>
        <span>{incidentLabel(match.incident)}</span>
        <span>{match.scoreLabel || (isComplete ? "Resultado cerrado" : "Score pendiente")}</span>
      </div>
    </article>
  );
}

function StageRoundColumn({ title, subtitle, rounds, entrantLookup, highlight = false, tone = "default", align = "start" }) {
  const toneClasses =
    tone === "winners"
      ? {
          shell: highlight
            ? "border-violet-300/60 bg-[linear-gradient(180deg,rgba(15,23,42,0.95),rgba(36,17,72,0.92),rgba(59,22,107,0.90))] text-white shadow-[0_24px_60px_rgba(76,29,149,0.28)]"
            : "border-violet-200/60 bg-[linear-gradient(180deg,rgba(250,245,255,0.98),rgba(243,232,255,0.88))] shadow-[0_20px_50px_rgba(76,29,149,0.10)]",
          eyebrow: highlight ? "text-violet-200/85" : "text-violet-700/72",
          pill: highlight ? "border-white/20 bg-white/10 text-white" : "border-violet-200 bg-white/90 text-violet-700",
          empty: highlight ? "border-white/20 text-violet-100/80" : "border-violet-200 text-violet-700/72",
          connector: highlight ? "bg-violet-300/50" : "bg-violet-300/35",
        }
      : tone === "losers"
        ? {
            shell: "border-cyan-200/70 bg-[linear-gradient(180deg,rgba(240,249,255,0.98),rgba(224,242,254,0.92))] shadow-[0_20px_50px_rgba(8,145,178,0.10)]",
            eyebrow: "text-cyan-700/78",
            pill: "border-cyan-200 bg-white/92 text-cyan-700",
            empty: "border-cyan-200 text-cyan-700/72",
            connector: "bg-cyan-300/45",
          }
        : tone === "final"
          ? {
              shell: "border-amber-300/60 bg-[linear-gradient(180deg,rgba(30,41,59,0.96),rgba(88,28,135,0.92),rgba(180,83,9,0.88))] text-white shadow-[0_30px_70px_rgba(180,83,9,0.24)]",
              eyebrow: "text-amber-200/88",
              pill: "border-white/20 bg-white/10 text-white",
              empty: "border-white/20 text-amber-100/82",
              connector: "bg-amber-300/55",
            }
          : {
              shell: highlight
                ? "border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] shadow-[0_20px_50px_rgba(15,23,42,0.08)]"
                : "border-slate-200/80 bg-[linear-gradient(180deg,rgba(255,255,255,0.98),rgba(248,250,252,0.96))] shadow-[0_20px_50px_rgba(15,23,42,0.08)]",
              eyebrow: highlight ? "text-[#0f4cc9]/72" : "text-slate-500",
              pill: "border-slate-200 bg-white text-slate-600",
              empty: "border-slate-300 text-slate-500",
              connector: "bg-slate-300/55",
            };

  return (
    <section className={`relative min-w-[280px] flex-1 rounded-[1.75rem] border p-4 ${toneClasses.shell}`}>
      <div className={`pointer-events-none absolute bottom-6 top-24 hidden w-px xl:block ${toneClasses.connector} ${align === "end" ? "-left-2" : align === "center" ? "left-1/2 -translate-x-1/2" : "-right-2"}`} />
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className={`text-[10px] font-semibold uppercase tracking-[0.28em] ${toneClasses.eyebrow}`}>
            {subtitle}
          </div>
          <h4 className="mt-2 text-lg font-black">{title}</h4>
        </div>
        <div className={`rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] ${toneClasses.pill}`}>
          {rounds.reduce((total, round) => total + (round.matches?.length || 0), 0)} matches
        </div>
      </div>

      <div className="mt-4 space-y-5">
        {rounds.map((round, roundIndex) => (
          <div
            key={round.roundId}
            className="relative space-y-3"
            style={{
              marginTop: roundIndex === 0 ? undefined : `${Math.min(roundIndex * 18, 42)}px`,
            }}
          >
            {roundIndex > 0 ? (
              <div className={`pointer-events-none absolute -top-4 left-4 right-4 h-px ${toneClasses.connector}`} />
            ) : null}
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className={`text-sm font-semibold ${highlight ? "text-white" : "text-slate-950"}`}>{round.name}</div>
                <div className={`text-[11px] ${highlight ? "text-violet-100/80" : "text-slate-500"}`}>
                  {stageLabel(round.stage)} · {round.matches.length} cruces
                </div>
              </div>
              <div className={`text-[11px] font-semibold ${highlight ? "text-cyan-200" : "text-[#0f4cc9]"}`}>
                {round.matches.filter((match) => match.status === "completed" || match.status === "bye").length}/{round.matches.length}
              </div>
            </div>
            <div className="space-y-3">
              {round.matches.map((match) => (
                <StageMatchCard
                  key={match.matchId}
                  match={match}
                  entrantLookup={entrantLookup}
                  featured={highlight}
                />
              ))}
            </div>
          </div>
        ))}
        {!rounds.length ? (
          <div className={`rounded-2xl border border-dashed p-4 text-sm ${toneClasses.empty}`}>
            Todavia no hay cruces publicados en esta etapa.
          </div>
        ) : null}
      </div>
    </section>
  );
}

function StreamStageBoard({
  currentRound,
  entrantLookup,
  mode,
  rounds,
  selectedTournament,
}) {
  const modeMeta = stageBoardModeMeta(mode);
  const groupedColumns = [];
  const roundRobinRounds = rounds.filter((round) => round.stage === "round_robin");
  const swissRounds = rounds.filter((round) => round.stage === "swiss");
  const topCutRounds = rounds.filter((round) => round.stage === "top_cut");
  const winnersRounds = rounds.filter((round) => round.stage === "winners");
  const losersRounds = rounds.filter((round) => round.stage === "losers");
  const grandFinalRounds = rounds.filter((round) => round.stage === "grand_final");
  const fallbackRounds = rounds.filter((round) => !["round_robin", "swiss", "top_cut", "winners", "losers", "grand_final"].includes(round.stage));

  if (mode === "swiss_top_cut") {
    groupedColumns.push({ title: "Swiss Lane", subtitle: "Regular season", rounds: swissRounds, tone: "losers", align: "start" });
    groupedColumns.push({ title: "Top Cut", subtitle: "Bracket final", rounds: topCutRounds, highlight: true, tone: "final", align: "end" });
  } else if (mode === "double_elimination") {
    groupedColumns.push({ title: "Winners", subtitle: "Upper bracket", rounds: winnersRounds, highlight: true, tone: "winners", align: "start" });
    groupedColumns.push({ title: "Losers", subtitle: "Lower bracket", rounds: losersRounds, tone: "losers", align: "center" });
    groupedColumns.push({ title: "Grand Final", subtitle: "Championship", rounds: grandFinalRounds, highlight: true, tone: "final", align: "end" });
  } else if (mode === "round_robin") {
    groupedColumns.push({ title: "Round Robin", subtitle: "League schedule", rounds: roundRobinRounds, highlight: true, tone: "losers", align: "start" });
  } else {
    groupedColumns.push({ title: "Main Bracket", subtitle: "Single elimination", rounds: fallbackRounds.length ? fallbackRounds : rounds, highlight: true, tone: "final", align: "start" });
  }

  const activeLabel = currentRound
    ? `${currentRound.name} en juego`
    : selectedTournament?.state?.championEntrantId
      ? "Campeon definido"
      : "Esperando siguiente accion";
  const stageStats = [
    ["Estado", selectedTournament?.state?.status || "setup"],
    ["Ronda activa", currentRound?.name || "Ninguna"],
    ["Publicacion", selectedTournament?.state?.publishedAt ? "Publicada" : "Pendiente"],
    ["Campeon", selectedTournament?.state?.championEntrantId ? "Definido" : "Pendiente"],
  ];

  return (
    <section className={`mt-5 overflow-hidden rounded-[2rem] border border-slate-900/80 bg-[radial-gradient(circle_at_top_left,rgba(109,40,217,0.28),transparent_28%),radial-gradient(circle_at_top_right,rgba(6,182,212,0.18),transparent_24%),linear-gradient(135deg,#070b16_0%,#0f172a_42%,#140f28_100%)] p-5 text-white shadow-[0_30px_90px_rgba(15,23,42,0.28)]`}>
      <div className={`pointer-events-none absolute inset-x-10 top-0 h-32 bg-gradient-to-r ${modeMeta.accent} blur-3xl`} />
      <div className="relative flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[11px] font-semibold uppercase tracking-[0.32em] text-violet-200/78">Stream / Stage Mode</div>
            <span className="rounded-full border border-white/12 bg-white/8 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-slate-100">
              {modeMeta.badge}
            </span>
          </div>
          <h3 className="mt-2 text-3xl font-black">{selectedTournament?.title || "Bracket board"}</h3>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-200/82">
            {modeMeta.description}
          </p>
          <div className="mt-4 text-xl font-black text-white/95">{modeMeta.headline}</div>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <span className="rounded-full border border-white/12 bg-white/8 px-3 py-1 uppercase tracking-[0.18em] text-slate-100">
            Formato: {tournamentModeLabel(mode)}
          </span>
          <span className="rounded-full border border-cyan-300/30 bg-cyan-400/10 px-3 py-1 uppercase tracking-[0.18em] text-cyan-100">
            {activeLabel}
          </span>
        </div>
      </div>

      <div className="relative mt-5 grid gap-3 md:grid-cols-[1.2fr_.8fr]">
        <div className="rounded-[1.6rem] border border-white/10 bg-white/6 p-4 backdrop-blur">
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-cyan-200/85">Production Notes</div>
          <div className="mt-3 grid gap-3 sm:grid-cols-3">
            <div className="rounded-2xl border border-white/10 bg-slate-950/20 p-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-300">Formato</div>
              <div className="mt-2 text-lg font-black text-white">{tournamentModeLabel(mode)}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/20 p-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-300">Narrativa</div>
              <div className="mt-2 text-sm font-semibold text-white/92">{modeMeta.badge}</div>
            </div>
            <div className="rounded-2xl border border-white/10 bg-slate-950/20 p-3">
              <div className="text-[10px] uppercase tracking-[0.24em] text-slate-300">Lectura activa</div>
              <div className="mt-2 text-sm font-semibold text-white/92">{activeLabel}</div>
            </div>
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          {stageStats.map(([label, value]) => (
            <div key={label} className="rounded-2xl border border-white/10 bg-white/6 p-3 backdrop-blur">
              <div className="text-[10px] font-semibold uppercase tracking-[0.24em] text-violet-200/76">{label}</div>
              <div className="mt-2 text-lg font-black text-white">{value}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="relative mt-4 flex flex-wrap gap-2 text-[11px] uppercase tracking-[0.2em] text-slate-300">
        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1">Slot vacío = open</span>
        <span className="rounded-full border border-cyan-300/20 bg-cyan-400/10 px-3 py-1 text-cyan-100">Resaltado = match ganador / tramo principal</span>
        <span className="rounded-full border border-amber-300/20 bg-amber-400/10 px-3 py-1 text-amber-100">Final lane = cierre de torneo</span>
      </div>

      <div className={`relative mt-6 flex flex-col gap-4 ${groupedColumns.length > 1 ? "xl:flex-row xl:items-start" : ""}`}>
        {groupedColumns.map((column) => (
          <StageRoundColumn
            key={column.title}
            title={column.title}
            subtitle={column.subtitle}
            rounds={column.rounds}
            entrantLookup={entrantLookup}
            highlight={column.highlight}
            tone={column.tone}
            align={column.align}
          />
        ))}
      </div>
    </section>
  );
}

function MatchCard({
  allowDraw = false,
  busyKey,
  entrantLookup,
  match,
  moveDraft,
  reportMatch,
  roundId,
  runAction,
  scoreDrafts,
  selectedEntrants,
  updateMoveDraft,
  updateScoreDraft,
}) {
  const left = entrantLookup[match.leftEntrantId];
  const right = entrantLookup[match.rightEntrantId];
  const leftDraftValue = moveDraft.leftEntrantId !== undefined ? moveDraft.leftEntrantId : (match.leftEntrantId || "");
  const rightDraftValue = moveDraft.rightEntrantId !== undefined ? moveDraft.rightEntrantId : (match.rightEntrantId || "");
  const canMarkLeftBye = Boolean(match.leftEntrantId) && (!match.rightEntrantId || rightDraftValue === "");
  const canMarkRightBye = Boolean(match.rightEntrantId) && (!match.leftEntrantId || leftDraftValue === "");
  const scheduleDraftValue =
    moveDraft.scheduledAt !== undefined ? moveDraft.scheduledAt : toDatetimeLocalValue(match.scheduledAt);

  return (
    <article className={PREL_SOFT_PANEL_CLASS + " p-3 shadow-sm"}>
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
            Match {match.matchNumber}
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Incidente: {incidentLabel(match.incident)} · Reportado: {fmtDate(match.completedAt)}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <div className="rounded-full border border-[#0f4cc9]/15 bg-[#edf4ff] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-[#0f4cc9]">
              {match.station || "Mesa por asignar"}
            </div>
            <div className="rounded-full border border-violet-200 bg-violet-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700">
              {fmtSchedulePill(match.scheduledAt)}
            </div>
          </div>
        </div>
        <span
          className={`rounded-full border px-2 py-1 text-[10px] font-semibold uppercase tracking-wide ${statusTone(match.status)}`}
        >
          {match.status}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2">
        {[
          { side: "left", entrant: left, entrantId: match.leftEntrantId },
          { side: "right", entrant: right, entrantId: match.rightEntrantId },
        ].map((slot) => (
          <button
            key={slot.side}
            disabled={!slot.entrantId || !!busyKey}
            onClick={() => reportMatch(match, slot.entrantId, slot.side)}
            className={`w-full rounded-xl border px-3 py-3 text-left transition disabled:cursor-not-allowed disabled:opacity-50 ${
              match.winnerEntrantId === slot.entrantId
                ? "border-emerald-300/55 bg-emerald-50 text-emerald-900 shadow-[0_0_0_1px_rgba(52,211,153,0.14)_inset]"
                : "border-[#0f4cc9]/12 bg-white text-slate-900 hover:border-[#0f4cc9]/30 hover:bg-[#f5f9ff] hover:shadow-[0_10px_24px_rgba(15,23,42,0.10)]"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium">{slot.entrant?.displayName || "BYE / vacio"}</div>
                <div className="text-xs text-slate-500">
                  {slot.entrant?.subtitle || "Sin detalle"}
                </div>
              </div>
              <div className="text-xs font-semibold uppercase tracking-wide">
                {match.winnerEntrantId === slot.entrantId ? "Ganador" : "Click para reportar"}
              </div>
            </div>
          </button>
        ))}
        {allowDraw && match.leftEntrantId && match.rightEntrantId ? (
          <button
            disabled={!!busyKey}
            onClick={() => reportMatch(match, "", "draw")}
            className="w-full rounded-xl border border-violet-300 bg-violet-50 px-3 py-3 text-left text-slate-900 transition hover:bg-violet-100 hover:shadow-[0_10px_24px_rgba(15,23,42,0.10)] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <div className="flex items-center justify-between gap-2">
              <div>
                <div className="font-medium">Reportar empate</div>
                <div className="text-xs text-slate-500">
                  Usa esta opcion si el match termina en draw dentro de Swiss.
                </div>
              </div>
              <div className="text-xs font-semibold uppercase tracking-wide text-violet-700">Empate</div>
            </div>
          </button>
        ) : null}
      </div>

      <div className="mt-3 grid grid-cols-1 gap-2 rounded-2xl border border-slate-200/70 bg-white/80 p-3 md:grid-cols-3">
        <input
          value={scoreDrafts[match.matchId]?.scoreLabel || match.scoreLabel || ""}
          onChange={(e) => updateScoreDraft(match.matchId, "scoreLabel", e.target.value)}
          placeholder="Score final, ej. 2-1"
          className={`${PREL_INPUT_CLASS} text-sm`}
        />
        <select
          value={scoreDrafts[match.matchId]?.incident || match.incident || "normal"}
          onChange={(e) => updateScoreDraft(match.matchId, "incident", e.target.value)}
          className={`${PREL_INPUT_CLASS} text-sm`}
        >
          {INCIDENT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
        <input
          value={scoreDrafts[match.matchId]?.notes || match.notes || ""}
          onChange={(e) => updateScoreDraft(match.matchId, "notes", e.target.value)}
          placeholder="Nota del match"
          className={`${PREL_INPUT_CLASS} text-sm`}
        />
      </div>

      <details className="mt-3 rounded-2xl border border-slate-200 bg-white/80 p-3">
        <summary className="cursor-pointer text-sm font-semibold text-slate-800">
          Ajustes manuales de este match
        </summary>
        <div className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-3">
          <select
            value={leftDraftValue}
            onChange={(e) => updateMoveDraft(match.matchId, "leftEntrantId", e.target.value)}
            className={`${PREL_INPUT_CLASS} text-sm`}
          >
            <option value="">BYE / vacio en slot izquierdo</option>
            {selectedEntrants
              .filter((entrant) => entrant.included)
              .map((entrant) => (
                <option key={entrant.entrantId} value={entrant.entrantId}>
                  {entrant.displayName}
                </option>
              ))}
          </select>
          <select
            value={rightDraftValue}
            onChange={(e) => updateMoveDraft(match.matchId, "rightEntrantId", e.target.value)}
            className={`${PREL_INPUT_CLASS} text-sm`}
          >
            <option value="">BYE / vacio en slot derecho</option>
            {selectedEntrants
              .filter((entrant) => entrant.included)
              .map((entrant) => (
                <option key={entrant.entrantId} value={entrant.entrantId}>
                  {entrant.displayName}
                </option>
              ))}
          </select>
          <button
            onClick={async () => {
              if (moveDraft.leftEntrantId !== undefined) {
                await runAction(
                  "move_match_entrant",
                  {
                    roundId,
                    matchId: match.matchId,
                    slot: "left",
                    entrantId: moveDraft.leftEntrantId,
                    notes: "Ajuste manual de bracket",
                  },
                  `move-left:${match.matchId}`
                );
              }
              if (moveDraft.rightEntrantId !== undefined) {
                await runAction(
                  "move_match_entrant",
                  {
                    roundId,
                    matchId: match.matchId,
                    slot: "right",
                    entrantId: moveDraft.rightEntrantId,
                    notes: "Ajuste manual de bracket",
                  },
                  `move-right:${match.matchId}`
                );
              }
            }}
            disabled={!!busyKey}
            className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-800 hover:bg-violet-100 disabled:opacity-60"
          >
            Aplicar ajuste
          </button>
        </div>
        <div className="mt-2 flex flex-wrap gap-2">
          <button
            onClick={() =>
              reportMatch(match, match.leftEntrantId, "bye-left")
            }
            disabled={!canMarkLeftBye || !!busyKey}
            className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Marcar BYE izquierda
          </button>
          <button
            onClick={() =>
              reportMatch(match, match.rightEntrantId, "bye-right")
            }
            disabled={!canMarkRightBye || !!busyKey}
            className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 hover:bg-sky-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Marcar BYE derecha
          </button>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto]">
          <input
            value={moveDraft.station ?? match.station ?? ""}
            onChange={(e) => updateMoveDraft(match.matchId, "station", e.target.value)}
            placeholder="Mesa 1, Estación A, Stream..."
            className={`${PREL_INPUT_CLASS} text-sm`}
          />
          <button
            onClick={() =>
              runAction(
                "assign_match_station",
                {
                  roundId,
                  matchId: match.matchId,
                  station: moveDraft.station ?? match.station ?? "",
                },
                `station:${match.matchId}`
              )
            }
            disabled={!!busyKey}
            className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-800 hover:bg-sky-100 disabled:opacity-60"
          >
            Guardar mesa
          </button>
        </div>
        <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-[1fr_auto_auto]">
          <input
            type="datetime-local"
            value={scheduleDraftValue}
            onChange={(e) => updateMoveDraft(match.matchId, "scheduledAt", e.target.value)}
            className={`${PREL_INPUT_CLASS} text-sm`}
          />
          <button
            onClick={() =>
              runAction(
                "assign_match_schedule",
                {
                  roundId,
                  matchId: match.matchId,
                  scheduledAt: moveDraft.scheduledAt !== undefined ? moveDraft.scheduledAt : scheduleDraftValue,
                },
                `schedule:${match.matchId}`
              )
            }
            disabled={!!busyKey}
            className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-800 hover:bg-violet-100 disabled:opacity-60"
          >
            Guardar fecha
          </button>
          <button
            onClick={() =>
              runAction(
                "assign_match_schedule",
                {
                  roundId,
                  matchId: match.matchId,
                  scheduledAt: "",
                },
                `schedule-clear:${match.matchId}`
              )
            }
            disabled={!!busyKey}
            className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            Limpiar
          </button>
        </div>
        <div className="mt-2 flex justify-end">
          <button
            onClick={() =>
              runAction(
                "reset_match",
                {
                  roundId,
                  matchId: match.matchId,
                  notes: "Reset manual para corregir resultado o bye",
                },
                `reset:${match.matchId}`
              )
            }
            disabled={!!busyKey}
            className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800 hover:bg-rose-100 disabled:opacity-60"
          >
            Resetear match
          </button>
        </div>
      </details>
    </article>
  );
}

export default function GamergyBracketManager({
  apiPath = "/api/tournament-ops/gamergy-brackets",
  tournamentFilter = "",
  enableExtendedModes = false,
}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);
  const [selectedTournamentId, setSelectedTournamentId] = useState("");
  const [draftEntrants, setDraftEntrants] = useState({});
  const [scoreDrafts, setScoreDrafts] = useState({});
  const [moveDrafts, setMoveDrafts] = useState({});
  const [busyKey, setBusyKey] = useState("");
  const [setupMessage, setSetupMessage] = useState("");
  const [workspaceMode, setWorkspaceMode] = useState("live");
  const [displayMode, setDisplayMode] = useState("ops");

  async function load(nextWorkspaceMode = workspaceMode) {
    setLoading(true);
    setError("");
    try {
      const token = await getAccessToken();
      if (!token) throw new Error("No pudimos validar tu acceso al panel de brackets.");
      const query = new URLSearchParams({ workspaceMode: nextWorkspaceMode });
      const res = await fetch(`${apiPath}?${query.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || "No se pudo cargar el administrador de brackets.");
      setPayload(json);
      setSetupMessage(json.setupMessage || "");
      setWorkspaceMode(json.workspaceMode || nextWorkspaceMode || "live");
      const tournaments = json.tournaments || [];
      setSelectedTournamentId((current) => current || tournaments[0]?.id || "");
      setDraftEntrants(
        Object.fromEntries(tournaments.map((tournament) => [tournament.id, tournament.state?.entrants || []]))
      );
    } catch (err) {
      setError(err.message || "No se pudo cargar el administrador de brackets.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const tournaments = useMemo(() => {
    const allTournaments = payload?.tournaments || [];
    const normalizedFilter = normalizeSearchText(tournamentFilter);
    if (!normalizedFilter) return allTournaments;
    return allTournaments.filter((item) => {
      const haystack = normalizeSearchText([
        item.title,
        item.displayLabel,
        item.slug,
        item.id,
        item.game,
        item.formatLabel,
        ...(Array.isArray(item.searchTerms) ? item.searchTerms : []),
      ].filter(Boolean).join(" "));
      return haystack.includes(normalizedFilter);
    });
  }, [payload?.tournaments, tournamentFilter]);
  const selectedTournament = useMemo(
    () => tournaments.find((item) => item.id === selectedTournamentId) || tournaments[0] || null,
    [tournaments, selectedTournamentId]
  );

  useEffect(() => {
    setSelectedTournamentId((current) => {
      if (tournaments.some((item) => item.id === current)) return current;
      return tournaments[0]?.id || "";
    });
  }, [tournaments]);

  const selectedEntrants = selectedTournament ? draftEntrants[selectedTournament.id] || [] : [];
  const includedEntrants = selectedEntrants.filter((entrant) => entrant.included !== false);
  const readyEntrants = selectedEntrants.filter(
    (entrant) => entrant.included !== false && (entrant.adminStatus || "active") === "active"
  );
  const entrantLookup = useMemo(
    () => Object.fromEntries(selectedEntrants.map((entrant) => [entrant.entrantId, entrant])),
    [selectedEntrants]
  );
  const duplicateEntrantMap = useMemo(() => buildDuplicateEntrantMap(selectedEntrants), [selectedEntrants]);
  const duplicateEntrants = useMemo(() => selectedEntrants.filter((entrant) => {
    const sourceKey = String(entrant.sourceEntryId || "").trim().toLowerCase();
    const nameKey = normalizeSearchText(entrant.displayName);
    const key = sourceKey ? `source:${sourceKey}` : `name:${nameKey}`;
    return (duplicateEntrantMap.get(key) || 0) > 1;
  }), [duplicateEntrantMap, selectedEntrants]);

  const rounds = selectedTournament?.state?.rounds || [];
  const swissRounds = rounds.filter((round) => round.stage === "swiss");
  const topCutRounds = rounds.filter((round) => round.stage === "top_cut");
  const roundRobinRounds = rounds.filter((round) => round.stage === "round_robin");
  const winnersRounds = rounds.filter((round) => round.stage === "winners");
  const losersRounds = rounds.filter((round) => round.stage === "losers");
  const grandFinalRounds = rounds.filter((round) => round.stage === "grand_final");
  const latestClosableRound = findLatestClosableRound(rounds);
  const latestClosableRoundIssueCount = getRoundIssueCount(latestClosableRound);
  const configuredSwissRounds = Math.max(0, Number(selectedTournament?.state?.settings?.swissRounds) || 0);
  const swissStageComplete = selectedTournament?.state?.mode === "swiss_top_cut"
    && configuredSwissRounds > 0
    && getSwissRounds(rounds).length >= configuredSwissRounds
    && !hasPendingMatches(getSwissRounds(rounds));
  const canCreateTopCut = selectedTournament?.state?.mode === "swiss_top_cut"
    && swissStageComplete
    && !topCutRounds.length;
  const currentRound = getCurrentRound(rounds);
  const completedMatches = getCompletedMatches(rounds);
  const totalMatches = getTotalMatches(rounds);
  const publishedAt = selectedTournament?.state?.publishedAt;
  const currentRoundPendingMatches = currentRound?.matches.filter(
    (match) => match.status !== "completed" && match.status !== "bye"
  ) || [];
  const currentRoundCompletedMatches = currentRound?.matches.filter(
    (match) => match.status === "completed" || match.status === "bye"
  ) || [];
  const currentRoundIssueMatches = currentRound?.matches.filter(
    (match) => (match.incident && match.incident !== "normal") || match.notes
  ) || [];
  const swissHistoryRounds = swissRounds.filter((round) => round.roundId !== currentRound?.roundId);
  const topCutHistoryRounds = topCutRounds.filter((round) => round.roundId !== currentRound?.roundId);
  const historyGroups = [
    ["Round robin", roundRobinRounds.filter((round) => round.roundId !== currentRound?.roundId)],
    ["Swiss", swissHistoryRounds],
    ["Winners bracket", winnersRounds.filter((round) => round.roundId !== currentRound?.roundId)],
    ["Losers bracket", losersRounds.filter((round) => round.roundId !== currentRound?.roundId)],
    ["Top cut / bracket", topCutHistoryRounds],
    ["Grand final", grandFinalRounds.filter((round) => round.roundId !== currentRound?.roundId)],
  ].filter(([, groupRounds]) => groupRounds.length);
  const stateUpdatedAt = selectedTournament?.state?.updatedAt;
  const unpublishedChanges = Boolean(
    stateUpdatedAt && (!publishedAt || new Date(stateUpdatedAt).getTime() > new Date(publishedAt).getTime())
  );
  const rosterLocked = Boolean(selectedTournament?.state?.settings?.rosterLocked);
  const isSandboxMode = workspaceMode === "sandbox";
  const sandboxStatus = selectedTournament?.state?.workspace || null;

  function updateEntrant(entrantId, key, value) {
    if (!selectedTournament) return;
    setDraftEntrants((prev) => ({
      ...prev,
      [selectedTournament.id]: (prev[selectedTournament.id] || []).map((entrant) =>
        entrant.entrantId === entrantId ? { ...entrant, [key]: value } : entrant
      ),
    }));
  }

  async function runAction(action, body = {}, nextBusyKey = action) {
    if (!selectedTournament) return;
    setError("");
    setBusyKey(nextBusyKey);
    try {
      const token = await getAccessToken();
      const res = await fetch(apiPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          action,
          tournamentId: selectedTournament.id,
          workspaceMode,
          ...body,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.ok) throw new Error(json.error || "No se pudo completar la accion del bracket.");
      setPayload(json);
      setSetupMessage(json.setupMessage || "");
      setWorkspaceMode(json.workspaceMode || workspaceMode);
      const nextTournaments = json.tournaments || [];
      setDraftEntrants(
        Object.fromEntries(nextTournaments.map((tournament) => [tournament.id, tournament.state?.entrants || []]))
      );
    } catch (err) {
      setError(err.message || "No se pudo completar la accion del bracket.");
    } finally {
      setBusyKey("");
    }
  }

  function updateMoveDraft(matchId, key, value) {
    setMoveDrafts((prev) => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || {}),
        [key]: value,
      },
    }));
  }

  function updateScoreDraft(matchId, key, value) {
    setScoreDrafts((prev) => ({
      ...prev,
      [matchId]: {
        ...(prev[matchId] || {}),
        [key]: value,
      },
    }));
  }

  function reportMatch(match, winnerEntrantId, side) {
    const currentIncident = scoreDrafts[match.matchId]?.incident || match.incident || "normal";
    const incident =
      side === "bye-left" || side === "bye-right"
        ? "bye"
        : currentIncident;

    return runAction(
      "report_match",
      {
        matchId: match.matchId,
        winnerEntrantId,
        scoreLabel:
          incident === "bye"
            ? (scoreDrafts[match.matchId]?.scoreLabel || match.scoreLabel || "BYE")
            : (scoreDrafts[match.matchId]?.scoreLabel || match.scoreLabel || ""),
        incident,
        notes: scoreDrafts[match.matchId]?.notes || match.notes || "",
      },
      `report:${match.matchId}:${side}`
    );
  }

  function primaryTournamentAction() {
    if (!selectedTournament) return null;
    if (currentRound) return null;
    if (latestClosableRound) {
      return {
        label: `Cerrar ${latestClosableRound.name}`,
        description:
          latestClosableRoundIssueCount > 0
            ? `Esta ronda ya está completa y tiene ${latestClosableRoundIssueCount} incidencia(s) documentada(s).`
            : "La ronda ya está completa y lista para cerrarse oficialmente.",
        action: () => runAction("close_current_round", { roundId: latestClosableRound.roundId }, `close-round:${latestClosableRound.roundId}`),
        tone: "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100",
      };
    }
    if (canCreateTopCut) {
      return {
        label: "Crear top cut",
        description: "Swiss terminó. El siguiente paso es abrir el bracket final desde standings.",
        action: () => runAction("advance_tournament", {}, "advance"),
        tone: "border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100",
      };
    }
    if (selectedTournament.state?.mode === "swiss_top_cut" && !currentRound && !topCutRounds.length) {
      return {
        label: "Generar siguiente ronda swiss",
        description: "No hay ronda abierta ahora mismo. Avanza el torneo para crear el próximo pairing.",
        action: () => runAction("advance_tournament", {}, "advance"),
        tone: "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100",
      };
    }
    if (selectedTournament.state?.championEntrantId && unpublishedChanges) {
      return {
        label: isSandboxMode ? "Cierre listo en prueba" : "Publicar cierre del torneo",
        description: isSandboxMode
          ? "Ya hay campeón en la copia de prueba. El hub real no se toca desde este modo."
          : "Ya hay campeón, pero todavía faltan cambios por publicarse en el hub.",
        action: isSandboxMode ? () => {} : () => runAction("publish_public", {}, "publish"),
        tone: isSandboxMode
          ? "border-violet-300 bg-violet-50 text-violet-800"
          : "border-transparent bg-[#0f4cc9] text-white hover:bg-[#0c3fa6]",
      };
    }
    return null;
  }

  function currentStepCopy() {
    if (!selectedTournament) return "Selecciona un torneo para comenzar.";
    if (!includedEntrants.length) return "Sincroniza y marca los participantes que van a jugar.";
    if (!rounds.length) return modeSetupInstruction(selectedTournament.state?.mode);
    if (currentRound) return `Ahora mismo toca correr ${currentRound.name} y reportar sus resultados.`;
    if (latestClosableRound) {
      return `La ${latestClosableRound.name} ya terminó. Ciérrala para dejar trazabilidad clara antes de seguir.`;
    }
    if (canCreateTopCut) {
      return "Swiss esta completo. El siguiente paso es crear el top cut desde standings.";
    }
    if (!selectedTournament.state?.championEntrantId) {
      return modeProgressInstruction(selectedTournament.state?.mode);
    }
    if (selectedTournament.state?.championEntrantId && unpublishedChanges) {
      return "El torneo ya tiene campeón, pero todavía faltan cambios por publicarse en el hub.";
    }
    if (!publishedAt) return "Ya puedes publicar standings y brackets al hub publico.";
    return "El torneo esta publicado. Si haces ajustes, vuelve a publicar para actualizar el hub.";
  }

  const contextualPrimaryAction = primaryTournamentAction();

  const quickActions = [
    {
      id: "sync",
      label: "1. Sincronizar jugadores",
      description: "Trae la lista mas reciente de registros al panel.",
      action: () => runAction("sync_entrants", {}, "sync"),
      tone: "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100",
    },
    {
      id: "save-entrants",
      label: "2. Guardar lista lista para jugar",
      description: "Confirma seeds, notas y estados antes de correr rondas.",
      action: () => runAction("save_entrants", { entrants: selectedEntrants }, "save-entrants"),
      tone: "border-[#0f4cc9]/25 bg-[#edf4ff] text-[#0f4cc9] hover:bg-[#dfeaff]",
    },
    {
      id: "next-round",
      label: selectedTournament?.state?.mode === "swiss_top_cut" ? "3. Avanzar Swiss" : "3. Generar bracket",
      description:
        selectedTournament?.state?.mode === "swiss_top_cut"
          ? "Cierra la ronda lista o genera el siguiente paso disponible del Swiss."
          : selectedTournament?.state?.mode === "double_elimination"
            ? "Crea winners bracket y deja listo el scaffold del losers bracket."
            : selectedTournament?.state?.mode === "round_robin"
              ? "Crea todas las rondas del calendario round robin."
              : "Crea el cuadro principal del torneo.",
      action: () =>
        selectedTournament?.state?.mode === "swiss_top_cut"
          ? runAction("advance_tournament", {}, "advance")
          : runAction("create_bracket", { entrants: selectedEntrants }, "elim-bracket"),
      tone:
        selectedTournament?.state?.mode === "swiss_top_cut"
          ? "border-sky-300 bg-sky-50 text-sky-800 hover:bg-sky-100"
          : "border-[#0f4cc9]/25 bg-[#edf4ff] text-[#0f4cc9] hover:bg-[#dfeaff]",
    },
    ...(canCreateTopCut
      ? [{
          id: "top-cut",
          label: "4. Crear top cut",
          description: "Usa los standings finales de Swiss para abrir el bracket definitivo.",
          action: () => runAction("create_top_cut", {}, "top-cut"),
          tone: "border-violet-300 bg-violet-50 text-violet-800 hover:bg-violet-100",
        }]
      : []),
    {
      id: "publish",
      label: canCreateTopCut ? "5. Publicar al hub" : "4. Publicar al hub",
      description: isSandboxMode ? "Desactivado en modo prueba para proteger el hub real." : "Empuja standings y brackets para que se vean afuera.",
      action: () => runAction("publish_public", {}, "publish"),
      tone: isSandboxMode ? "border-violet-300 bg-violet-50 text-violet-800" : "border-transparent bg-[#0f4cc9] text-white hover:bg-[#0c3fa6]",
      disabled: isSandboxMode,
    },
  ];

  if (loading) {
    return (
      <section className={`${PREL_PANEL_CLASS} p-5`}>
        <div className="text-slate-600">Cargando administrador de brackets...</div>
      </section>
    );
  }

  return (
    <StaticPanel className="p-4 md:p-5">
      <div className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:items-start lg:justify-between">
        <div>
          <h2 className="text-xl font-bold">Operacion de torneo</h2>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Este panel es para correr el torneo en vivo: preparar jugadores, generar pairings o brackets,
            reportar resultados, corregir incidentes y publicar lo que se ve en el hub publico.
          </p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1">
            <button
              onClick={() => load("live")}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${!isSandboxMode ? "bg-[#0f4cc9] text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              Estado real
            </button>
            <button
              onClick={() => load("sandbox")}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${isSandboxMode ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              Modo prueba
            </button>
          </div>
          <select
            value={selectedTournament?.id || ""}
            onChange={(e) => setSelectedTournamentId(e.target.value)}
            className={`w-full sm:w-auto ${PREL_INPUT_CLASS}`}
          >
            {tournaments.map((tournament) => (
              <option key={tournament.id} value={tournament.id}>
                {tournament.title}
              </option>
            ))}
          </select>
          <button
            onClick={load}
            className="rounded-lg border border-[#0f4cc9]/20 bg-white px-4 py-2 text-[#0f4cc9] hover:bg-[#edf4ff]"
          >
            Refrescar
          </button>
          <div className="inline-flex rounded-lg border border-slate-300 bg-white p-1">
            <button
              onClick={() => setDisplayMode("ops")}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${displayMode === "ops" ? "bg-[#0f4cc9] text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              Ops mode
            </button>
            <button
              onClick={() => setDisplayMode("stage")}
              className={`rounded-md px-3 py-2 text-sm font-semibold ${displayMode === "stage" ? "bg-violet-600 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            >
              Stream / Stage
            </button>
          </div>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-rose-300 bg-rose-50 p-3 text-rose-800">{error}</div>
      ) : null}

      {setupMessage ? (
        <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-amber-800">
          {setupMessage}
        </div>
      ) : null}

      {selectedTournament && isSandboxMode ? (
        <div className="mt-4 rounded-xl border border-violet-300 bg-violet-50 p-3 text-violet-900">
          <div className="font-semibold">Modo prueba activo</div>
          <div className="mt-1 text-sm">
            Todo lo que hagas aqui se guarda en una copia privada para tu usuario y no toca el bracket real ni el hub publico.
          </div>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full border border-violet-200 bg-white/80 px-3 py-1">
              Sandbox listo: {sandboxStatus?.hasSandboxState ? "Si" : "Usando base clonable del live"}
            </span>
            <span className="rounded-full border border-violet-200 bg-white/80 px-3 py-1">
              Ultimo update sandbox: {fmtDate(sandboxStatus?.sandboxUpdatedAt)}
            </span>
            <span className="rounded-full border border-violet-200 bg-white/80 px-3 py-1">
              Live intacto hasta: {fmtDate(sandboxStatus?.liveUpdatedAt)}
            </span>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              onClick={() => runAction("sandbox_clone_live", {}, "sandbox-clone")}
              disabled={!!busyKey}
              className="rounded-lg border border-violet-300 bg-white px-3 py-2 text-sm font-semibold text-violet-800 hover:bg-violet-100 disabled:opacity-60"
            >
              Clonar estado real a prueba
            </button>
            <button
              onClick={() => runAction("sandbox_reset", {}, "sandbox-reset")}
              disabled={!!busyKey}
              className="rounded-lg border border-slate-300 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
            >
              Resetear prueba desde live
            </button>
          </div>
        </div>
      ) : null}

      {selectedTournament ? (
        <>
          <section className="mt-5 rounded-2xl border border-[#0f4cc9]/15 bg-[linear-gradient(135deg,rgba(15,76,201,0.10),rgba(255,255,255,0.90),rgba(239,68,68,0.08))] p-4 md:p-5">
            <div className="grid gap-4 lg:grid-cols-[1.2fr_.8fr]">
              <div>
                <div className="text-xs uppercase tracking-[0.24em] text-[#0f4cc9]/80">Que sigue</div>
                <h3 className="mt-2 text-2xl font-black text-slate-950">{currentStepCopy()}</h3>
                <div className="mt-3 flex flex-wrap gap-2 text-xs text-slate-700">
                  <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1">
                    Formato: {tournamentModeLabel(selectedTournament.state?.mode)}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1">
                    Estado: {selectedTournament.state?.status || "setup"}
                  </span>
                  <span className="rounded-full border border-slate-200 bg-white/80 px-3 py-1">
                    Ronda activa: {currentRound?.name || "Ninguna ahora mismo"}
                  </span>
                  <span className={`rounded-full border px-3 py-1 ${unpublishedChanges ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
                    Publicación: {isSandboxMode ? "Bloqueada en modo prueba" : unpublishedChanges ? "Hay cambios sin publicar" : "Todo publicado"}
                  </span>
                  <span className="rounded-full border border-violet-200 bg-violet-50 px-3 py-1 text-violet-800">
                    Vista: {displayMode === "stage" ? "Stream / Stage" : "Ops interna"}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {[
                  ["Jugadores activos", readyEntrants.length],
                  ["Rondas creadas", rounds.length],
                  ["Matches completados", `${completedMatches}/${totalMatches || 0}`],
                  [
                    "Publicado",
                    isSandboxMode ? "Sandbox" : publishedAt ? "Si" : "No",
                  ],
                ].map(([label, value]) => (
                  <div key={label} className={`${PREL_SOFT_PANEL_CLASS} p-3`}>
                    <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                    <div className="mt-2 text-2xl font-black text-slate-950">{value}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {displayMode === "stage" ? (
            <StreamStageBoard
              currentRound={currentRound}
              entrantLookup={entrantLookup}
              mode={selectedTournament.state?.mode}
              rounds={rounds}
              selectedTournament={selectedTournament}
            />
          ) : null}

          <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            {[
              ["Registrados", selectedTournament.entrantsSummary?.totalEntrants || 0],
              ["Incluidos", selectedTournament.entrantsSummary?.includedEntrants || 0],
              ["Listos", selectedTournament.entrantsSummary?.readyEntrants || 0],
              ["Drops", selectedTournament.entrantsSummary?.droppedEntrants || 0],
              [
                "DQ / No-show",
                (selectedTournament.entrantsSummary?.dqEntrants || 0) +
                  (selectedTournament.entrantsSummary?.noShowEntrants || 0),
              ],
              ["Campeon", selectedTournament.state?.championEntrantId ? "Definido" : "Pendiente"],
            ].map(([label, value]) => (
              <div key={label} className={`${PREL_SOFT_PANEL_CLASS} p-3`}>
                <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
                <div className="mt-2 text-2xl font-black">{value}</div>
              </div>
            ))}
          </div>

          <div className={`mt-5 grid gap-4 xl:grid-cols-[1.1fr_.9fr] ${displayMode === "stage" ? "opacity-95" : ""}`}>
            <StaticPanel className="bg-[#f8fbff] p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-lg font-bold">Flujo rapido para staff</h3>
                  <p className="mt-1 text-sm text-slate-600">
                    Usa estas acciones en orden cuando estes corriendo el torneo en vivo.
                  </p>
                </div>
              </div>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                {contextualPrimaryAction ? (
                  <button
                    onClick={contextualPrimaryAction.action}
                    disabled={!!busyKey || (isSandboxMode && contextualPrimaryAction.label === "Cierre listo en prueba")}
                    className={`rounded-2xl border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-60 hover:shadow-[0_12px_30px_rgba(0,0,0,0.18)] ${contextualPrimaryAction.tone}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">{contextualPrimaryAction.label}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">Siguiente paso</div>
                    </div>
                    <div className="mt-1 text-sm opacity-90">{contextualPrimaryAction.description}</div>
                  </button>
                ) : null}
                {quickActions.map((item) => (
                  <button
                    key={item.id}
                    onClick={item.action}
                    disabled={!!busyKey || !!item.disabled}
                    className={`rounded-2xl border p-4 text-left transition disabled:cursor-not-allowed disabled:opacity-60 hover:shadow-[0_12px_30px_rgba(0,0,0,0.22)] ${item.tone}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">{item.label}</div>
                      <div className="text-[10px] uppercase tracking-[0.22em] opacity-75">Accion</div>
                    </div>
                    <div className="mt-1 text-sm opacity-90">{item.description}</div>
                  </button>
                ))}
              </div>
              {unpublishedChanges ? (
                <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
                  Hay cambios sin publicar desde {fmtDate(stateUpdatedAt)}. Antes de compartir resultados afuera, vuelve a usar
                  {" "}
                  <span className="font-semibold">Publicar al hub</span>.
                </div>
              ) : null}
              <div className={`mt-4 rounded-xl p-3 text-sm ${isSandboxMode ? "border border-violet-300 bg-violet-50 text-violet-900" : "border border-amber-300 bg-amber-50 text-amber-800"}`}>
                {isSandboxMode
                  ? "En modo prueba no se publica nada al hub. Puedes ensayar brackets, seeds, BYEs y scheduling sin ensuciar el torneo real."
                  : "Si editas resultados o haces ajustes manuales despues de publicado, vuelve a usar Publicar al hub para refrescar la vista publica."}
              </div>
            </StaticPanel>

            <StaticPanel className="bg-[#f8fbff] p-4">
              <h3 className="text-lg font-bold">Configuracion y control</h3>
              <div className="mt-4 grid gap-3 lg:grid-cols-3">
                <label className="text-sm text-slate-700">
                  Formato del torneo
                  <select
                    value={selectedTournament.state?.mode || "single_elimination"}
                    onChange={(e) =>
                      runAction(
                        "save_settings",
                        {
                          mode: e.target.value,
                          swissRounds: selectedTournament.state?.settings?.swissRounds,
                          topCutSize: selectedTournament.state?.settings?.topCutSize,
                        },
                        "save-settings"
                      )
                    }
                    className={`mt-1 w-full ${PREL_INPUT_CLASS}`}
                  >
                    <option value="single_elimination">Eliminacion simple</option>
                    {enableExtendedModes ? <option value="double_elimination">Doble eliminacion</option> : null}
                    {enableExtendedModes ? <option value="round_robin">Round robin</option> : null}
                    <option value="swiss_top_cut">Swiss + Top cut</option>
                  </select>
                </label>
                {selectedTournament.state?.mode === "swiss_top_cut" ? (
                  <>
                    <label className="text-sm text-slate-700">
                      Rondas swiss
                      <input
                        type="number"
                        min="1"
                        defaultValue={selectedTournament.state?.settings?.swissRounds || 5}
                        onBlur={(e) =>
                          runAction(
                            "save_settings",
                            {
                              mode: selectedTournament.state?.mode,
                              swissRounds: e.target.value,
                              topCutSize: selectedTournament.state?.settings?.topCutSize,
                            },
                            "save-settings"
                          )
                        }
                        className={`mt-1 w-full ${PREL_INPUT_CLASS}`}
                      />
                    </label>
                    <label className="text-sm text-slate-700">
                      Tamano del top cut
                      <input
                        type="number"
                        min="2"
                        defaultValue={selectedTournament.state?.settings?.topCutSize || 8}
                        onBlur={(e) =>
                          runAction(
                            "save_settings",
                            {
                              mode: selectedTournament.state?.mode,
                              swissRounds: selectedTournament.state?.settings?.swissRounds,
                              topCutSize: e.target.value,
                            },
                            "save-settings"
                          )
                        }
                        className={`mt-1 w-full ${PREL_INPUT_CLASS}`}
                      />
                    </label>
                  </>
                ) : (
                  <div className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm text-slate-600 shadow-sm lg:col-span-2">
                    Esta configuracion usa <strong>{tournamentModeLabel(selectedTournament.state?.mode)}</strong>. Los ajustes de Swiss y top cut se esconden porque no aplican a este formato.
                  </div>
                )}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={rosterLocked}
                    onChange={(e) =>
                      runAction(
                        "save_settings",
                        {
                          mode: selectedTournament.state?.mode,
                          swissRounds: selectedTournament.state?.settings?.swissRounds,
                          topCutSize: selectedTournament.state?.settings?.topCutSize,
                          rosterLocked: e.target.checked,
                        },
                        "toggle-roster-lock"
                      )
                    }
                  />
                  Roster lock
                </label>
                <div className={`rounded-full border px-3 py-1 text-xs ${rosterLocked ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-800"}`}>
                  {rosterLocked ? "Seeds y lista bloqueados para operación" : "Roster editable antes de cerrar operación"}
                </div>
              </div>

              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm text-slate-700 shadow-sm">
                  <div className="font-semibold text-slate-950">Ultima sincronizacion</div>
                  <div className="mt-1">{fmtDate(selectedTournament.state?.lastSyncedAt)}</div>
                </div>
                <div className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm text-slate-700 shadow-sm">
                  <div className="font-semibold text-slate-950">Ultima publicacion</div>
                  <div className="mt-1">{fmtDate(selectedTournament.state?.publishedAt)}</div>
                </div>
                <div className={`rounded-xl border p-3 text-sm shadow-sm ${unpublishedChanges ? "border-amber-300 bg-amber-50 text-amber-800" : "border-emerald-200 bg-emerald-50 text-emerald-800"}`}>
                  <div className="font-semibold">Estado de publicación</div>
                  <div className="mt-1">{unpublishedChanges ? "Hay cambios internos pendientes por publicar." : "La vista pública ya refleja el estado actual."}</div>
                </div>
                <div className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm text-slate-700 shadow-sm">
                  <div className="font-semibold text-slate-950">Bracket / top cut generado</div>
                  <div className="mt-1">{fmtDate(selectedTournament.state?.bracketGeneratedAt)}</div>
                </div>
                <div className="rounded-xl border border-slate-200/80 bg-white p-3 text-sm text-slate-700 shadow-sm">
                  <div className="font-semibold text-slate-950">Campeon actual</div>
                  <div className="mt-1">
                    {selectedTournament.state?.championEntrantId
                      ? entrantLookup[selectedTournament.state.championEntrantId]?.displayName ||
                        selectedTournament.state.championEntrantId
                      : "Todavia sin campeon"}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {selectedTournament.state?.mode === "swiss_top_cut" ? (
                  canCreateTopCut ? (
                    <button
                      onClick={() => runAction("create_top_cut", {}, "top-cut")}
                      disabled={!!busyKey}
                      className="rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm text-violet-800 hover:bg-violet-100 disabled:opacity-60"
                    >
                      Crear top cut desde standings
                    </button>
                  ) : (
                    <div className="rounded-lg border border-violet-200 bg-violet-50/70 px-3 py-2 text-sm text-violet-800">
                      El top cut se habilita cuando Swiss termine y no queden rounds pendientes.
                    </div>
                  )
                ) : null}
                {selectedTournament.state?.mode === "double_elimination" ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50/80 px-3 py-2 text-sm text-amber-800">
                    Los cruces del losers bracket se pueden terminar de acomodar con los ajustes manuales de cada match.
                  </div>
                ) : null}
                <button
                  onClick={() => runAction("reset_bracket", {}, "reset-bracket")}
                  disabled={!!busyKey}
                  className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800 hover:bg-rose-100 disabled:opacity-60"
                >
                  Resetear torneo
                </button>
              </div>
            </StaticPanel>
          </div>

          <section className="mt-5 rounded-[1.9rem] border border-emerald-200/90 bg-[linear-gradient(135deg,rgba(16,185,129,0.10),rgba(255,255,255,0.98),rgba(236,253,245,0.98))] p-5 shadow-[0_22px_50px_rgba(16,185,129,0.08)]">
            <SectionHeading
              eyebrow="Mesa activa"
              title={currentRound ? currentRound.name : "No hay ronda corriendo ahora mismo"}
              description="Esta zona esta pensada para operar en piso: revisar pairings, marcar ganadores rapido y atender excepciones sin perderte en el resto del panel."
              aside={
                <div className="flex flex-wrap gap-2">
                  {currentRound ? (
                    <button
                      onClick={() => runAction("publish_public", {}, "publish")}
                      disabled={!!busyKey || isSandboxMode}
                      className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
                    >
                      Publicar estado actual
                    </button>
                  ) : null}
                  {selectedTournament.state?.mode === "swiss_top_cut" && !currentRound ? (
                    <button
                      onClick={() => runAction("create_next_swiss_round", {}, "swiss-round")}
                      disabled={!!busyKey}
                      className="rounded-xl border border-sky-300 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-60"
                    >
                      {nextRoundActionLabel(selectedTournament.state?.mode)}
                    </button>
                  ) : null}
                </div>
              }
            />

            {currentRound ? (
              <>
                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <SummaryStat label="Mesas pendientes" value={currentRoundPendingMatches.length} tone="alert" />
                  <SummaryStat label="Mesas cerradas" value={currentRoundCompletedMatches.length} tone="success" />
                  <SummaryStat label="Casos a revisar" value={currentRoundIssueMatches.length} tone="alert" />
                  <SummaryStat label="Total de pairings" value={currentRound.matches.length} />
                </div>

                <div className="mt-4 grid gap-4 xl:grid-cols-[1.15fr_.85fr]">
                  <div className={PREL_DARK_TEXT_PANEL_CLASS}>
                    <SectionHeading
                      title="Pairings que faltan por cerrar"
                      description="Marca ganador tocando el jugador correcto. Usa score e incidente solo si hace falta documentarlo."
                      aside={
                        <div className="flex flex-wrap gap-2">
                          <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                            {currentRoundPendingMatches.length} abiertos
                          </div>
                          <button
                            onClick={() =>
                              runAction(
                                "auto_assign_stations",
                                { roundId: currentRound.roundId, prefix: "Mesa", start: 1 },
                                `auto-stations:${currentRound.roundId}`
                              )
                            }
                            disabled={!!busyKey}
                            className="rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-60"
                          >
                            Auto asignar mesas
                          </button>
                        </div>
                      }
                    />

                    <div className="mt-4 space-y-3">
                      {currentRoundPendingMatches.map((match) => (
                        <MatchCard
                          allowDraw={["swiss", "round_robin"].includes(currentRound.stage)}
                          key={match.matchId}
                          busyKey={busyKey}
                          entrantLookup={entrantLookup}
                          match={match}
                          moveDraft={moveDrafts[match.matchId] || {}}
                          reportMatch={reportMatch}
                          roundId={currentRound.roundId}
                          runAction={runAction}
                          scoreDrafts={scoreDrafts}
                          selectedEntrants={selectedEntrants}
                          updateMoveDraft={updateMoveDraft}
                          updateScoreDraft={updateScoreDraft}
                        />
                      ))}
                      {!currentRoundPendingMatches.length ? (
                        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
                          Todas las mesas de esta ronda estan cerradas. Si todo esta correcto, ya puedes pasar a la siguiente accion del torneo.
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className={PREL_DARK_TEXT_PANEL_CLASS}>
                      <SectionHeading
                        title="Excepciones y ajustes"
                        description="Aqui caen los matches con incidentes o notas para que el juez o el admin los revise rapido."
                      />
                      <div className="mt-4 space-y-3">
                        {currentRoundIssueMatches.map((match) => (
                          <div key={match.matchId} className="rounded-xl border border-amber-200 bg-amber-50 p-3">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-amber-900">Match {match.matchNumber}</div>
                              <div className="text-xs text-amber-700">{incidentLabel(match.incident)}</div>
                            </div>
                            <div className="mt-2 text-sm text-slate-800">
                              {(entrantLookup[match.leftEntrantId]?.displayName || "BYE / vacio")}
                              {" vs "}
                              {(entrantLookup[match.rightEntrantId]?.displayName || "BYE / vacio")}
                            </div>
                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#0f4cc9]">
                              {match.station || "Mesa por asignar"}
                            </div>
                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-violet-700">
                              {fmtSchedulePill(match.scheduledAt)}
                            </div>
                            <div className="mt-1 text-xs text-slate-600">{match.notes || "Sin nota adicional"}</div>
                          </div>
                        ))}
                        {!currentRoundIssueMatches.length ? (
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                            No hay incidentes marcados en la ronda activa.
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <div className={PREL_DARK_TEXT_PANEL_CLASS}>
                      <SectionHeading
                        title="Mesas ya reportadas"
                        description="Resumen rapido de resultados ya cerrados para validar que todo este cayendo bien."
                      />
                      <div className="mt-4 space-y-2">
                        {currentRoundCompletedMatches.slice(0, 8).map((match) => (
                          <div key={match.matchId} className="rounded-xl border border-slate-200/80 bg-slate-50/90 p-3 text-sm">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-semibold text-slate-900">Match {match.matchNumber}</div>
                              <div className="text-xs text-slate-500">{match.scoreLabel || incidentLabel(match.incident)}</div>
                            </div>
                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-[#0f4cc9]">
                              {match.station || "Mesa por asignar"}
                            </div>
                            <div className="mt-1 text-xs font-semibold uppercase tracking-wide text-violet-700">
                              {fmtSchedulePill(match.scheduledAt)}
                            </div>
                            <div className="mt-1 text-slate-700">
                              {(entrantLookup[match.winnerEntrantId]?.displayName || "Resultado pendiente")} gano este pairing
                            </div>
                          </div>
                        ))}
                        {!currentRoundCompletedMatches.length ? (
                          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                            Aun no se han reportado mesas cerradas en esta ronda.
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <StaticPanel className="mt-4 border-slate-200 bg-white/90 p-4 text-sm text-slate-700">
                Cuando generes la proxima ronda o el bracket, esta area se convierte en la vista principal de mesa con pairings activos, incidentes y resultados.
              </StaticPanel>
            )}
          </section>

          <section className={`mt-5 ${PREL_SECTION_CLASS}`}>
            <SectionHeading
              eyebrow="Roster"
              title="Jugadores y equipos"
              description="Aqui decides quien entra al torneo, en que seed sale y cualquier observacion importante para el staff."
              aside={
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => runAction("sync_entrants", {}, "sync")}
                    disabled={!!busyKey}
                    className="rounded-xl border border-sky-300 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 hover:bg-sky-100 disabled:opacity-60"
                  >
                    Sincronizar registros
                  </button>
                  <button
                    onClick={() => runAction("save_entrants", { entrants: selectedEntrants }, "save-entrants")}
                    disabled={!!busyKey}
                    className="rounded-xl border border-cyan-300 bg-cyan-50 px-3 py-2 text-sm font-semibold text-cyan-800 hover:bg-cyan-100 disabled:opacity-60"
                  >
                    Guardar lista
                  </button>
                </div>
              }
            />

            <div className="mt-4 flex flex-wrap gap-2 text-xs text-slate-600">
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                Incluidos: {includedEntrants.length}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                Listos para jugar: {readyEntrants.length}
              </span>
              <span className={`rounded-full border px-3 py-1 ${duplicateEntrants.length ? "border-rose-300 bg-rose-50 text-rose-800" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
                Duplicados detectados: {duplicateEntrants.length}
              </span>
              <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                Revisar drops, DQ y no-show antes de generar rondas
              </span>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <button
                onClick={() => runAction("bulk_update_entrants", { bulkAction: "normalize_seeds" }, "bulk-seeds")}
                disabled={!!busyKey}
                className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800 hover:bg-slate-50 disabled:opacity-60"
              >
                Normalizar seeds
              </button>
              <button
                onClick={() => runAction("bulk_update_entrants", { bulkAction: "include_checked_in" }, "bulk-checkin")}
                disabled={!!busyKey}
                className="rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100 disabled:opacity-60"
              >
                Incluir solo checked-in
              </button>
              <button
                onClick={() => runAction("bulk_update_entrants", { bulkAction: "remove_duplicates" }, "bulk-dedupe")}
                disabled={!!busyKey}
                className="rounded-xl border border-rose-300 bg-rose-50 px-3 py-2 text-sm font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-60"
              >
                Quitar repetidos
              </button>
            </div>

            <div className={`mt-4 overflow-hidden ${PREL_INSET_CLASS}`}>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-semibold text-slate-900">Control de participantes</div>
                <div className="text-xs text-slate-500">Edita seeds, estados y notas sin salir del juego actual.</div>
              </div>
              <div className="overflow-x-auto">
              <table className="w-full min-w-[860px] text-sm">
                <thead className={PREL_TABLE_HEAD_CLASS}>
                  <tr className="border-b border-slate-200">
                    <th className="px-3 py-2 text-left">Juega</th>
                    <th className="px-3 py-2 text-left">Seed</th>
                    <th className="px-3 py-2 text-left">Participante</th>
                    <th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Estado</th>
                    <th className="px-3 py-2 text-left">Check-in</th>
                    <th className="px-3 py-2 text-left">Notas para staff</th>
                    <th className="px-3 py-2 text-left">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedEntrants.map((entrant) => (
                    <tr key={entrant.entrantId} className={PREL_TABLE_ROW_CLASS}>
                      <td className="px-3 py-3">
                        <input
                          type="checkbox"
                          checked={entrant.included !== false}
                          onChange={(e) => updateEntrant(entrant.entrantId, "included", e.target.checked)}
                          disabled={rosterLocked}
                        />
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="1"
                          value={entrant.seed || ""}
                          onChange={(e) => updateEntrant(entrant.entrantId, "seed", e.target.value)}
                          disabled={rosterLocked}
                          className="w-20 rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-900 disabled:opacity-60"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <div className="font-medium text-slate-900">
                          {entrant.displayName}
                          {duplicateEntrants.some((item) => item.entrantId === entrant.entrantId) ? (
                            <span className="ml-2 rounded-full border border-rose-300 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-rose-700">
                              Duplicado
                            </span>
                          ) : null}
                        </div>
                        <div className="text-xs text-slate-500">{entrant.subtitle || "Sin detalle adicional"}</div>
                      </td>
                      <td className="px-3 py-3 capitalize text-slate-700">{entrant.entryType}</td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <select
                            value={entrant.adminStatus || "active"}
                            onChange={(e) => updateEntrant(entrant.entrantId, "adminStatus", e.target.value)}
                            className="min-w-[8rem] rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-900"
                          >
                            {ADMIN_STATUS_OPTIONS.map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                          <button
                            onClick={() =>
                              runAction(
                                "update_entrant_status",
                                {
                                  entrantId: entrant.entrantId,
                                  adminStatus: entrant.adminStatus,
                                  notes: entrant.notes || "",
                                },
                                `entrant-status:${entrant.entrantId}`
                              )
                            }
                            disabled={!!busyKey}
                            className="shrink-0 rounded-lg border border-slate-300 bg-slate-50 px-2 py-1 text-xs text-slate-700 hover:bg-slate-100 disabled:opacity-60"
                          >
                            Aplicar
                          </button>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-slate-700">{entrant.checkinStatus || "pending"}</td>
                      <td className="px-3 py-3">
                        <input
                          value={entrant.notes || ""}
                          onChange={(e) => updateEntrant(entrant.entrantId, "notes", e.target.value)}
                          className="w-full rounded-lg border border-slate-300 bg-white px-2 py-1 text-slate-900"
                          placeholder="Ej. llego tarde, cambio de nombre, validado"
                        />
                      </td>
                      <td className="px-3 py-3">
                        <button
                          onClick={() =>
                            runAction("remove_entrant", { entrantId: entrant.entrantId }, `remove-entrant:${entrant.entrantId}`)
                          }
                          disabled={!!busyKey}
                          className="rounded-lg border border-rose-300 bg-rose-50 px-2 py-1 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-60"
                        >
                          Quitar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
              {!selectedEntrants.length ? (
                <p className="mt-4 text-sm text-slate-500">Todavia no hay participantes listados para este torneo.</p>
              ) : null}
            </div>
          </section>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1fr_.95fr]">
            <section className={PREL_SECTION_CLASS}>
              <SectionHeading
                eyebrow="Ranking"
                title="Standings"
                description="Esta tabla se actualiza con los resultados reportados. Sirve para swiss, round robin y referencia operativa del resto de formatos."
              />
              <div className={`mt-4 overflow-hidden ${PREL_INSET_CLASS}`}>
                <div className="mb-3 text-sm font-semibold text-slate-900">Posiciones en vivo</div>
                <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-sm">
                  <thead className={PREL_TABLE_HEAD_CLASS}>
                    <tr className="border-b border-slate-200">
                      <th className="px-3 py-2 text-left">#</th>
                      <th className="px-3 py-2 text-left">Participante</th>
                      <th className="px-3 py-2 text-left">Record</th>
                      <th className="px-3 py-2 text-left">Puntos</th>
                      <th className="px-3 py-2 text-left">OMW</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selectedTournament.standings || []).map((item) => (
                      <tr key={item.entrantId} className={PREL_TABLE_ROW_CLASS}>
                        <td className="px-3 py-3 text-slate-700">{item.rank}</td>
                        <td className="px-3 py-3">
                          <div className="font-medium text-slate-900">{item.displayName}</div>
                          <div className="text-xs text-slate-500">{item.subtitle || "Sin detalle"}</div>
                        </td>
                        <td className="px-3 py-3 text-slate-700">{item.record}</td>
                        <td className="px-3 py-3 text-slate-700">{item.matchPoints}</td>
                        <td className="px-3 py-3 text-slate-700">{(item.omw * 100).toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </section>

            <section className={PREL_SECTION_CLASS}>
              <SectionHeading
                eyebrow="Auditoria"
                title="Bitacora de acciones"
                description="Aqui queda registro de quien hizo cambios, cuando y que accion corrio."
              />
              <div className="mt-4 space-y-3 max-h-[28rem] overflow-auto pr-1">
                {(selectedTournament.state?.auditLog || [])
                  .slice()
                  .reverse()
                  .map((entry) => (
                    <article key={entry.id} className="rounded-xl border border-slate-200/80 bg-slate-50/90 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="font-semibold text-slate-900">{entry.summary}</div>
                        <div className="text-xs text-slate-500">{fmtDate(entry.at)}</div>
                      </div>
                      <div className="mt-2 text-xs text-slate-600">
                        {entry.actorEmail} · {entry.actorRole} · {entry.action}
                      </div>
                    </article>
                  ))}
                {!selectedTournament.state?.auditLog?.length ? (
                  <p className="text-sm text-slate-500">Todavia no hay acciones registradas.</p>
                ) : null}
              </div>
            </section>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-2">
            {(historyGroups.length ? historyGroups : [["Historial", []]]).map(([groupLabel, groupRounds]) => (
              <section key={groupLabel} className={PREL_SECTION_CLASS}>
                <SectionHeading
                  eyebrow="Historial"
                  title={groupLabel}
                  description="Historial de rondas y cruces ya creados. La ronda activa se trabaja arriba en la vista de mesa."
                />

                <div className="mt-4 space-y-4">
                  {groupRounds.map((round) => (
                    <div key={round.roundId} className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <div>
                          <h4 className="font-bold text-slate-950">{round.name}</h4>
                          <div className="mt-1 text-xs uppercase tracking-wide text-slate-500">
                            {stageLabel(round.stage)} · {round.matches.length} matches
                          </div>
                        </div>
                        <div className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs text-slate-600">
                          Completados:{" "}
                          {round.matches.filter((match) => match.status === "completed" || match.status === "bye").length}/
                          {round.matches.length}
                        </div>
                      </div>

                      <div className="mt-4 space-y-3">
                        {round.matches.map((match) => {
                          return (
                            <MatchCard
                              allowDraw={["swiss", "round_robin"].includes(round.stage)}
                              key={match.matchId}
                              busyKey={busyKey}
                              entrantLookup={entrantLookup}
                              match={match}
                              moveDraft={moveDrafts[match.matchId] || {}}
                              reportMatch={reportMatch}
                              roundId={round.roundId}
                              runAction={runAction}
                              scoreDrafts={scoreDrafts}
                              selectedEntrants={selectedEntrants}
                              updateMoveDraft={updateMoveDraft}
                              updateScoreDraft={updateScoreDraft}
                            />
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  {!groupRounds.length ? (
                    <p className="text-sm text-slate-500">Todavia no hay rondas en esta seccion.</p>
                  ) : null}
                </div>
              </section>
            ))}
          </div>
        </>
      ) : (
        <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800">
          {tournamentFilter
            ? `No encontramos torneos configurados para "${tournamentFilter}" en este panel.`
            : "No encontramos torneos configurados para este panel."}
        </div>
      )}
    </StaticPanel>
  );
}
